import React, { useMemo } from 'react';
import { v4 } from 'uuid';

import { getDailyPercentageValue } from 'common/helpers';

import './TrendChart.css';
import { defaultTrendChartTheme } from './constants';

export type Color = {
  line: string;
  start: string;
  end: string;
  middle: string;
};
type TrendChartProps = {
  trend: number[] | undefined;
  first: number | undefined;
  price: number | undefined;
  className?: string;
  width?: number;
  height?: number;
  strokeWidth?: string | number;
  colors?: {
    green: Color;
    red: Color;
    gray: Color;
  };
};

export const TrendChart: React.FC<React.PropsWithChildren<TrendChartProps>> = React.memo((props) => {
  const {
    className = 'trend__chart',
    width = 270,
    height = 100,
    strokeWidth = 4,
    colors = defaultTrendChartTheme,
    trend = [],
    first = 0,
    price = 0,
  } = props;

  const dailyChange = useMemo(() => getDailyPercentageValue(price, first), [first, price]);

  let color: { start: string; middle: string; end: string; line: string } = colors.gray;

  if (dailyChange >= 0) color = colors.green;
  if (dailyChange < 0) color = colors.red;

  const min = useMemo(() => Math.min(...trend), [trend]);

  let maxY = Math.max(...trend) - min || 1;
  const maxX = useMemo(() => trend.length - 1, [trend.length]);

  let deltaY = maxY / height;
  const deltaX = useMemo(() => maxX / width, [maxX, width]);

  if (maxY === min) {
    deltaY = min / height;
    maxY = min;
  }

  let pointsLine: string | number[] = [];
  let pointsGradient: string | number[] = [0, maxY / deltaY];

  for (let i = 0; i < trend.length; i++) {
    const value = (trend[i] - min) / deltaY;
    pointsLine.push(i / deltaX, height - (1 / 100) * value * 100);
  }

  pointsGradient = pointsGradient.concat(pointsLine);
  pointsGradient.push(pointsGradient[pointsGradient.length - 2], maxY / deltaY);

  pointsLine = pointsLine.join(',');
  pointsGradient = pointsGradient.join(',');

  const gradientId = useMemo(() => v4(), []);

  const isTrendEmpty = useMemo(() => trend.every((value) => value === trend[0]), [trend]);

  if (!trend.length) return <div className='trend__chart trend__chart--loading' />;

  if (isTrendEmpty) return <div className={className} />;

  return (
    <svg className={className} viewBox={`0 0 ${width} ${height}`}>
      <linearGradient id={`${gradientId}`} x2='0' y2='100%'>
        <stop stopColor={color.start} offset='0' />
        <stop stopColor={color.middle} offset='10%' />
        <stop stopColor={color.end} offset='100%' />
      </linearGradient>

      <polyline fill='none' stroke={color.line} strokeWidth={strokeWidth} points={`${pointsLine}`} />
      <polyline fill={`url(#${gradientId})`} stroke='none' strokeWidth='none' points={`${pointsGradient}`} />
    </svg>
  );
});
